import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Users, Volume2, Map as MapIcon, Columns3, Route, ClipboardList, Settings2, LayoutList, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import { SellBlocked } from "@/components/TrialBanner";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg, fmtDateTime, PAY_LABELS, isManagerRole, isAdminRole, waLink } from "@/lib/api";
import { directPrint } from "@/lib/qz";
import { PrintAlerts } from "@/components/delivery/PrintAlerts";
import { isOrderSound, setOrderSound } from "@/lib/orderSound";
import { deliveryOrder } from "@/lib/receipts";
import { PanelLink } from "@/components/CallButton";
import { toast } from "sonner";
import { DeliveryOrdersMap } from "@/components/delivery/DeliveryOrdersMap";
import { RouteDialog } from "@/components/delivery/RouteDialog";
import { CourierDayDialog } from "@/components/delivery/CourierDayDialog";
import { DeliverySettingsPanel } from "@/components/delivery/DeliverySettingsPanel";
import { NewOrderDialog } from "@/components/delivery/NewOrderDialog";
import { departureText, ratingText, trackUrl } from "@/lib/route";
import { OrdersBoard } from "@/components/delivery/board/OrdersBoard";
import { NoCourierAlert } from "@/components/delivery/board/NoCourierAlert";
import { CouriersDialog } from "@/components/delivery/couriers/CouriersDialog";
import { TYPE } from "@/components/delivery/board/status";


export const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.25].forEach((t) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.value = 880; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.3, ctx.currentTime + t); g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.2);
      o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.22);
    });
  } catch { /* sem áudio */ }
};

function PrintOrder({ o, store }) {
  return (
    <div className="hidden print:block print-area p-2 font-mono text-xs">
      <p className="text-center text-base font-bold">*** REIMPRESSÃO ***</p>
      <p className="text-center">2ª via · {fmtDateTime(new Date().toISOString())}</p>
      <p className="border-t border-dashed my-2" />
      <p className="text-center font-bold">{store}</p>
      <p className="text-center font-bold">PEDIDO #{o.number} · {TYPE[o.type]?.[1]}</p>
      <p>{fmtDateTime(o.created_at)}</p>
      <p>Cliente: {o.customer.name} {o.customer.phone}</p>
      {o.type === "delivery" && <p>Endereço: {o.customer.address} {o.zone && `(${o.zone})`}</p>}
      {o.type === "delivery" && o.pickup_code && <p className="text-center font-bold">CÓDIGO DE COLETA: {o.pickup_code}</p>}
      {o.table_number && <p>Mesa: {o.table_number}</p>}{o.comanda_number && <p>Comanda: {o.comanda_number}</p>}
      <p className="border-t border-dashed my-2" />
      {o.items.map((i) => <div key={i.id}><div className="flex justify-between"><span>{i.qty}x {i.name}</span><span>{brl(i.total)}</span></div>{i.notes && <p> obs: {i.notes}</p>}</div>)}
      <p className="border-t border-dashed my-2" />
      {o.delivery_fee > 0 && <p>Entrega: {brl(o.delivery_fee)}</p>}
      {o.payment_fee > 0 && <p>Taxa da forma de pagamento: {brl(o.payment_fee)}</p>}
      {o.coupon && <p>Cupom {o.coupon.code} ({o.coupon.label}): - {brl(o.coupon.saved)}</p>}
      <p className="font-bold">TOTAL {brl(o.total)} · {PAY_LABELS[o.payment_method]}{o.online?.status === "approved" ? " · PAGO ONLINE" : ""}{o.change_for > 0 ? ` · troco p/ ${brl(o.change_for)}` : ""}</p>
      {o.notes && <p>Obs.: {o.notes}</p>}
    </div>
  );
}


export default function Delivery() {
  const [orders, setOrders] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [showCouriers, setShowCouriers] = useState(false);
  const [printing, setPrinting] = useState(null);
  const [sound, setSound] = useState(isOrderSound);
  const [view, setView] = useState("quadro");
  const [routing, setRouting] = useState(false);
  const [dayReport, setDayReport] = useState(false);
  const [dInfo, setDInfo] = useState({ module: false, whatsapp: false, menu_enabled: true });
  const known = useRef(null);
  const autoSeen = useRef(null);
  const { session, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const admin = isAdminRole(session.user);
  const autoOn = !!session.tenant.settings.delivery_auto_accept;
  const tab = admin && params.get("aba") === "area" ? "area" : "pedidos";
  const showMap = session.tenant.settings.delivery_area_mode === "mapa" || orders.some((o) => o.customer?.lat != null || o.tracking?.lat != null);
  const loc = session.tenant.settings.store_location || {};
  const notify = (o) => waLink(o.customer.phone, departureText(o, session.tenant.nome_fantasia, trackUrl(session.tenant.menu_slug, o)));
  const askRating = (o) => waLink(o.customer.phone, ratingText(o, session.tenant.nome_fantasia, trackUrl(session.tenant.menu_slug, o)));
  const toggleSound = () => {
    setOrderSound(!sound);
    setSound(!sound);
    toast.success(sound ? "Som dos pedidos desligado neste computador" : "Som dos pedidos ligado neste computador");
  };
  const load = useCallback(() => api.get("/orders").then((r) => {
    const fresh = r.data.filter((o) => o.status === "recebido").map((o) => o.id);
    if (known.current && fresh.some((id) => !known.current.includes(id))) toast.info("Novo pedido recebido!");
    known.current = fresh;
    const auto = r.data.filter((o) => o.auto_accepted && o.status === "confirmado");
    if (autoSeen.current) {
      const news = auto.filter((o) => !autoSeen.current.includes(o.id));
      if (news.length) toast.info(`Pedido aceito automaticamente: #${news.map((o) => o.number).join(", #")}`);
    }
    autoSeen.current = auto.map((o) => o.id);
    setOrders(r.data);
  }), []);
  const toggleAuto = async () => {
    try {
      await api.put("/settings/delivery", { settings: { delivery_auto_accept: !autoOn } });
      await refresh();
      toast.success(autoOn ? "Aceite automático desligado" : "Aceite automático ligado: pedidos do cardápio já entram confirmados");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const loadCouriers = useCallback(() => api.get("/couriers").then((r) => setCouriers(r.data)), []);
  const loadInfo = useCallback(() => api.get("/delivery/dispatch-status").then((r) => setDInfo(r.data)).catch(() => {}), []);
  useEffect(() => { loadInfo(); }, [loadInfo]);
  const callCouriers = async (o) => {
    try {
      const { data } = await api.post(`/delivery/dispatch/${o.id}`);
      toast.success(data.dispatch?.status === "offering" ? `Entregadores chamados para o pedido #${o.number}` : "Nenhum entregador ativo com WhatsApp cadastrado");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const pauseMenu = async (enabled) => {
    if (!enabled && !window.confirm("Bloquear as vendas do cardápio digital? Os clientes não conseguem fazer pedidos até você liberar.")) return;
    try {
      await api.post("/delivery/menu", { enabled });
      await refresh();
      loadInfo();
      toast.success(enabled ? "Vendas do cardápio liberadas" : "Vendas do cardápio bloqueadas");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const dispatchProps = { info: dInfo, onCall: callCouriers, onPause: pauseMenu };
  useEffect(() => {
    load(); loadCouriers();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load, loadCouriers]);
  useEffect(() => { if (printing) { setTimeout(() => { window.print(); setPrinting(null); }, 100); } }, [printing]);
  const move = async (o, status) => {
    let reason = "";
    if (status === "cancelado") { reason = window.prompt(`Motivo para ${o.status === "recebido" ? "recusar" : "cancelar"} o pedido #${o.number}:`); if (reason === null) return; }
    try {
      await api.patch(`/orders/${o.id}/status`, { status, reason });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const reprint = async (o) => {
    if (await directPrint(session, "delivery", (pr) => deliveryOrder(o, session.tenant.nome_fantasia, pr, true))) toast.success(`Pedido #${o.number} reenviado para a impressora`);
    else setPrinting(o);
  };
  const edit = async (o, body) => { try { await api.patch(`/orders/${o.id}`, body); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <>
      <PageHeader eyebrow="Pedidos" title="Delivery" subtitle={tab === "area" ? "Taxas, pedido mínimo, prazos, área de entrega no mapa e rota do entregador." : `Atualiza a cada 10s · delivery ${session.tenant.settings.delivery_enabled ? "ativo" : "desativado"}.`}
        actions={tab === "area" ? null : <>
          {isManagerRole(session.user) && <Button variant="outline" onClick={() => setDayReport(true)} data-testid="courier-day-btn"><ClipboardList className="h-4 w-4 mr-1" />Entregas do dia</Button>}
          {session.tenant.settings.courier_route_enabled && <Button variant="outline" onClick={() => setRouting(true)} data-testid="courier-route-btn"><Route className="h-4 w-4 mr-1" />Rota do entregador</Button>}
          {showMap && <Button variant="outline" onClick={() => setView((v) => (v === "mapa" ? "quadro" : "mapa"))} data-testid="toggle-map-view-btn">{view === "mapa" ? <><Columns3 className="h-4 w-4 mr-1" />Quadro</> : <><MapIcon className="h-4 w-4 mr-1" />Mapa</>}</Button>}
          <Button variant={autoOn ? "default" : "outline"} onClick={toggleAuto} disabled={!admin} title={admin ? "" : "Só administradores podem alterar"} className={autoOn ? "bg-emerald-600 text-white hover:bg-emerald-600/90" : ""} data-testid="auto-accept-toggle-btn">
            <Zap className="h-4 w-4 mr-1" />Aceite automático: {autoOn ? "ligado" : "desligado"}
          </Button>
          <Button variant="outline" onClick={toggleSound} title={sound ? "Som dos pedidos ligado neste computador" : "Som dos pedidos desligado neste computador"} data-testid="toggle-sound-btn"><Volume2 className={`h-4 w-4 ${sound ? "" : "opacity-40"}`} /></Button>
          {isManagerRole(session.user) && <Button variant="outline" onClick={() => setShowCouriers(true)} data-testid="couriers-btn"><Users className="h-4 w-4 mr-1" />Entregadores</Button>}
          <PanelLink slug={session.tenant.menu_slug} />
          <Button onClick={() => setCreating(true)} data-testid="new-order-btn"><Plus className="h-4 w-4 mr-1" />Novo pedido</Button>
        </>} />
      <SellBlocked />
      {admin && (
        <div className="flex w-fit flex-wrap rounded-lg border p-1" data-testid="delivery-tabs">
          {[["pedidos", "Pedidos", LayoutList], ["area", "Área de entrega e taxas", Settings2]].map(([k, l, Icon]) => (
            <button key={k} onClick={() => setParams(k === "area" ? { aba: "area" } : {})} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`delivery-tab-${k}`}><Icon className="h-4 w-4" />{l}</button>
          ))}
        </div>
      )}
      {tab !== "area" && <PrintAlerts enabled={!!session.tenant.settings.printing?.enabled} />}
      {tab !== "area" && <NoCourierAlert orders={orders} info={dInfo} onPause={pauseMenu} onSelectCall={callCouriers} />}
      {tab === "area" ? <DeliverySettingsPanel /> : showMap && view === "mapa" ? <DeliveryOrdersMap settings={session.tenant.settings} orders={orders} /> : (
        <OrdersBoard orders={orders} steps={session.tenant.settings.order_steps || {}} couriers={couriers} onMove={move} onEdit={edit} onPrint={reprint} notify={notify} askRating={askRating} onCalled={load} dispatch={dispatchProps} />
      )}
      {printing && <PrintOrder o={printing} store={session.tenant.nome_fantasia} />}
      <NewOrderDialog open={creating} onOpenChange={setCreating} onCreated={load} />
      <CouriersDialog open={showCouriers} onOpenChange={setShowCouriers} couriers={couriers} reload={loadCouriers} />
      {isManagerRole(session.user) && <CourierDayDialog open={dayReport} onOpenChange={setDayReport} />}
      {session.tenant.settings.courier_route_enabled && <RouteDialog open={routing} onOpenChange={setRouting} orders={orders} couriers={couriers} store={loc.lat != null ? [loc.lat, loc.lng] : null} storeName={session.tenant.nome_fantasia} notify={notify} onDone={load} />}
    </>
  );
}
